import { Component, OnInit } from "@angular/core";
import { AuthClienteService } from "src/app/services/authCliente.service";
import { NodeService } from "src/app/services/node.service";
import { GlobalService } from "src/app/shared/global.service";
import { Router } from "@angular/router";
import { StorageService } from "src/app/services/storage.service";
import { FireService } from "src/app/services/fire.service";
import { PwaInstallService } from "src/app/services/pwa-install.service";
import { firstValueFrom } from "rxjs";
import { LoginSubmitPayload } from "src/app/shared/components/login-shell/login-shell.component";
import { getShareableBaseUrl } from "src/app/shared/utils/url";
import { encodeCustomLink, decodeCustomLink } from "src/app/shared/utils/custom-link";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit {

  username = "";
  loading = false;
  loginError: string | null = null;
  successMessage = false;

  // Code-entry flow (PWA fallback quando il deep-link non apre la PWA, es.
  // iOS standalone): utente inserisce il codice ricevuto via email e viene
  // autenticato tramite /users/verifyCode che ritorna lo stesso `link` base64
  // del magic-link tradizionale.
  codeLoading = false;
  codeError: string | null = null;

  // SECURITY: niente prefetch di `fire.getCliente` sul blur dell'email —
  // getCliente RUOTA la pwd del magic link (scrittura Firestore), quindi un
  // semplice blur invalidava i link già inviati. La query parte solo al submit.

  // ######################################
  // FUNCTION: constructor
  constructor(
    private auth: AuthClienteService,
    private g: GlobalService,
    private node: NodeService,
    private router: Router,
    private storage: StorageService,
    private fire: FireService,
    private pwaInstall: PwaInstallService,
  ) { }

  // ######################################
  // FUNCTION: isStandalonePwaSupported
  // True solo quando la pagina è aperta in una PWA standalone su iOS/iPadOS
  // o macOS: in quel contesto il deep-link del magic-link aprirebbe Safari
  // anziché la PWA, quindi il backend deve includere il codice 6-char e
  // omettere il pulsante "Accedi". Altrove (browser/Android) il link basta.
  private isStandalonePwaSupported(): boolean {
    if (!this.pwaInstall.isStandalone()) return false;
    if (this.pwaInstall.isIOS()) return true;
    const ua = window.navigator.userAgent || "";
    return /Macintosh/.test(ua);
  }

  // ######################################
  // FUNCTION: ngOnInit
  // PRESERVED BYTE-FOR-BYTE from the original - this is the email-link
  // auto-login path and is the only way legitimate users from emailed
  // links get in. Captcha is NOT applied here (user already clicked an
  // emailed link; backend validates via deadline + pwd check).
  ngOnInit(): void {
    // Variante PWA cliente: chi installa da /login ottiene una PWA
    // dedicata con start_url=/login e id distinto da quella admin.
    this.pwaInstall.setManifest("cliente");

    const token = this.storage.getToken();
    if (token) {
      let decoded: any;
      try {
        decoded = decodeCustomLink(token);
      } catch (err: any) {
        // SECURITY: mai loggare porzioni del token (contiene la pwd del magic link).
        console.error('[Login] token decode error', err);
        this.storage.saveToken('');
        this.loginError = "Link non valido o corrotto.";
        return;
      }
      console.debug('[Login] decoded payload', {
        hasPwd: !!decoded?.pwd,
        hasDeadline: !!decoded?.deadline,
        azienda: decoded?.azienda,
        reparti: decoded?.reparti,
        utenti: decoded?.utenti,
        keys: Object.keys(decoded || {}),
      });
      const currentDate = Date.now();
      const deadline = decoded.deadline;

      // Check if the deadline is less than today's date
      if (deadline >= currentDate) {
        // Mostra subito il loader 4H (heart/head/hands/results) del login-shell
        // mentre validiamo credenziali + exchange-link. Senza questo, l'utente
        // vede il form vuoto per 1-2s prima del redirect a /questionari.
        this.loading = true;

        // PWD RESYNC: fra creazione link e click, un altro flusso può aver
        // rigenerato la pwd in Firestore (es. `fire.getCliente` triggerato
        // da un nuovo submit sulla login page) → checkPassword
        // fallirebbe con pwd mismatch benché il link sia legittimo. Scriviamo
        // la pwd del link in Firestore PRIMA di checkPassword: la deadline
        // (sopra) già gate la validità temporale, quindi un link leaked
        // scaduto resta morto e non c'è escalation di privilegi.
        this.fire.updateSubDoc(
          'clienti', 'reparti', 'utenti',
          { pwd: decoded.pwd },
          decoded.azienda, decoded.reparti, decoded.utenti
        ).catch(err => console.debug('[Login] pwd resync failed', err))
          .then(() => this.fire.checkPassword(decoded.utenti, decoded.azienda, decoded.reparti, decoded.pwd))
          .then(async isValid => {
            if (isValid) {
              decoded.deadline = GlobalService.getDeadline(24 * 60);
              // Exchange the LinkAccess payload for a real JWT (used by protected
              // endpoints like /users/sendLink). Stored in a separate slot so it
              // does not collide with the legacy base64 "token".
              // AuthClienteGuard richiede l'apiToken: la sessione (saveUser) si
              // apre SOLO dopo aver ottenuto il JWT, altrimenti il guard
              // rimbalzerebbe l'utente sul login senza spiegazioni.
              let apiToken: string | null = null;
              try {
                const exchangeRes: any = await firstValueFrom(
                  this.node.callNodeUrl(
                    {
                      azienda: decoded.azienda,
                      reparti: decoded.reparti,
                      utenti: decoded.utenti,
                      pwd: decoded.pwd,
                    },
                    '/clienti/exchange-link'
                  )
                );
                if (exchangeRes?.token && typeof exchangeRes.token === "string") {
                  apiToken = exchangeRes.token;
                } else {
                  console.warn('[Login] exchange-link: nessun token ricevuto');
                }
              } catch (jwtErr) {
                console.error('[Login] exchange-link error', jwtErr);
              }
              if (!apiToken) {
                this.loading = false;
                this.loginError = "Accesso non riuscito. Richiedi un nuovo link di accesso.";
                return;
              }
              this.storage.saveApiToken(apiToken);
              this.auth.saveUser(decoded);
              this.router.navigateByUrl("/questionari");
            } else {
              this.loading = false;
              this.loginError = "Credenziali non valide.";
            }
          })
          .catch(() => {
            this.loading = false;
            this.loginError = "Credenziali non valide.";
          });
      }
      else {
        this.g.createErrorNotification("Accesso Scaduto", "Invia il link di accesso per email");
        this.storage.saveToken('');
      }
    }

    this.loginError = null;
  }

  // ######################################
  // FUNCTION: onShellSubmit
  // Called by <app-login-shell> when the user submits the email form.
  // Sequential dependency: getCliente output -> LinkAccess payload - cannot
  // be parallelized. Captcha token is added to LinkAccess only (Firestore
  // query is not bot-vulnerable on its own).
  async onShellSubmit(payload: LoginSubmitPayload): Promise<void> {
    this.username = payload.user;

    if (!this.g.checkEmail(this.username)) {
      this.loginError = "Inserisci un indirizzo email valido.";
      return;
    }

    this.loading = true;
    this.loginError = null;

    let formData: any = {
      user: this.username,
      type: "admin",
    };

    try {
      // getCliente ruota la pwd del magic link: chiamato SOLO qui (submit).
      const res = await firstValueFrom(this.fire.getCliente(formData));
      if (res && Object.keys(res).length > 0) {
        formData = { ...formData, ...res };
        const encodedUrl = encodeCustomLink(formData);

        if (formData.user) {
          try {
            await firstValueFrom(
              this.node.callNodeUrl(
                {
                  user: formData.user,
                  link: encodedUrl,
                  captcha: payload.captchaToken,
                  // Permette al backend di costruire il link email con l'origin
                  // corrente invece del suo APP_URL hardcoded su prod — utile
                  // in dev (localhost:4200) e su collapp.
                  baseUrl: getShareableBaseUrl(),
                  // Gate esclusivo: PWA standalone iOS/iPadOS/macOS riceve
                  // email con SOLO codice (niente deep-link che aprirebbe
                  // Safari anziché la PWA). Browser normale riceve email
                  // con SOLO link cliccabile (niente codice = niente rumore).
                  fromStandalonePwa: this.isStandalonePwaSupported(),
                },
                "/users/LinkAccess"
              )
            );
            this.successMessage = true;
          } catch (mailErr: any) {
            const detail = mailErr?.detail;
            if (mailErr?.status === 429) {
              this.loginError = typeof detail === "string"
                ? detail
                : detail?.message || "Troppi tentativi. Riprova più tardi.";
            } else {
              this.loginError = "Errore durante l'invio del link. Riprova.";
            }
          }
        }
      } else {
        this.loginError = "Nessun account trovato con questo indirizzo email.";
      }
    } catch (error: any) {
      const detail = error?.detail;
      if (error?.status === 429) {
        this.loginError = typeof detail === "string"
          ? detail
          : detail?.message || "Troppi tentativi. Riprova più tardi.";
      } else {
        this.loginError = "Si è verificato un errore. Riprova.";
      }
    } finally {
      this.loading = false;
    }
  }

  // ######################################
  // FUNCTION: onShellCode
  // Handler del pulsante "Accedi con codice" della login-shell (visibile solo
  // in PWA standalone). Chiama /users/verifyCode che consuma il codice,
  // restituisce `{link, token}` (JWT già mintato backend-side) e salviamo
  // direttamente la sessione cliente senza passare da /login/:token (che
  // soffriva di race sulla pwd quando `fire.getCliente` la ruotava).
  async onShellCode(code: string): Promise<void> {
    if (!code) return;
    this.codeLoading = true;
    this.codeError = null;
    try {
      const res: any = await firstValueFrom(
        this.node.callNodeUrl({ code }, "/users/verifyCode")
      );
      const link = res?.link;
      const jwtToken = res?.token;
      if (!link || typeof link !== "string") {
        this.codeError = "Risposta del server non valida.";
        return;
      }

      let decoded: any = null;
      try {
        decoded = decodeCustomLink(link);
      } catch (decodeErr) {
        console.error("[onShellCode] link decode error", decodeErr);
        this.codeError = "Risposta del server non valida.";
        return;
      }

      // AuthClienteGuard richiede l'apiToken: senza JWT la sessione verrebbe
      // chiusa al primo guard → meglio un errore esplicito qui.
      if (!jwtToken || typeof jwtToken !== "string") {
        this.codeError = "Accesso non riuscito. Richiedi un nuovo codice.";
        return;
      }
      this.storage.saveApiToken(jwtToken);
      if (decoded) {
        decoded.deadline = GlobalService.getDeadline(24 * 60);
        this.auth.saveUser(decoded);
        this.storage.saveToken(link);
      }
      this.router.navigateByUrl("/questionari");
      return;
    } catch (err: any) {
      const status = err?.status;
      const detail = err?.detail;
      if (status === 404) {
        this.codeError = "Codice non trovato. Verifica e riprova.";
      } else if (status === 410) {
        const msg = typeof detail === "string" ? detail : "";
        this.codeError = msg || "Codice scaduto o già utilizzato.";
      } else if (status === 429) {
        this.codeError = "Troppi tentativi. Riprova più tardi.";
      } else {
        this.codeError = "Errore durante la verifica del codice.";
      }
    } finally {
      this.codeLoading = false;
    }
  }

}
